import { cva, type VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'ring-offset-background transition-[color,background-color,box-shadow,transform] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:bg-disabled disabled:text-disabled-foreground disabled:border-disabled disabled:active:scale-100',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        // `brand` — the headline CTA. Uses the cyan-mint accent + brand-glow
        // shadow on hover so the page's primary action visibly outranks
        // every other Card/Button on screen.
        brand: [
          'bg-brand text-brand-foreground hover:bg-brand-hover',
          'shadow-1 hover:shadow-brand focus-visible:ring-brand',
        ].join(' '),
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-brand underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        // Default = 44px to meet WCAG 2.5.5 (AA) target size — matters for
        // gym use with sweaty fingers / gloves on small phones. `compact`
        // keeps the previous 40px height for inline secondary actions where
        // 44px would overpower the surrounding text.
        default: 'h-11 px-4 py-2',
        compact: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-12 rounded-md px-6 text-base',
        xl: 'h-14 rounded-lg px-8 text-lg',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
